'use client';

import { c as _c } from "react/compiler-runtime";
import type { ReactNode } from 'react';
import type { FieldShellRenderArgs, FieldShellSize } from '@/components/ui/field-shell';
import { FieldShell } from '@/components/ui/field-shell';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectSeparator, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '@/lib/ui/utils';
export type SelectFieldItem = {
  type?: 'item' | undefined;
  value: string;
  label: ReactNode;
  disabled?: boolean | undefined;
};
export type SelectFieldSeparator = {
  type: 'separator';
  key: string;
};
export type SelectFieldGroup = {
  type: 'group';
  label: string;
  items: ReadonlyArray<SelectFieldItem | SelectFieldSeparator>;
};
export type SelectFieldOption = SelectFieldItem | SelectFieldSeparator | SelectFieldGroup;
export interface SelectFieldProps {
  label?: ReactNode | undefined;
  description?: string | undefined;
  error?: string | undefined;
  size?: FieldShellSize | undefined;
  fullWidth?: boolean | undefined;
  required?: boolean | undefined;
  disabled?: boolean | undefined;
  id?: string | undefined;
  name?: string | undefined;
  value: string | undefined;
  /** Explicit trigger content for grouped or rich options whose label cannot
   * be inferred until the portalled option list mounts. */
  valueLabel?: ReactNode | undefined;
  onValueChange: (value: string) => void;
  placeholder?: string | undefined;
  items: ReadonlyArray<SelectFieldOption>;
  containerClassName?: string | undefined;
  labelClassName?: string | undefined;
  descriptionClassName?: string | undefined;
  errorClassName?: string | undefined;
  triggerClassName?: string | undefined;
  /** Forwarded to the underlying trigger. */
  autoComplete?: string | undefined;
  /** Accessible name when no visible `label` is rendered. */
  'aria-label'?: string | undefined;
  /** Communicates required state when a composed label renders its own visible indicator. */
  'aria-required'?: boolean | undefined;
}
function isItem(option: SelectFieldOption): option is SelectFieldItem {
  return option.type === undefined || option.type === 'item';
}
function isSeparator(option: SelectFieldOption): option is SelectFieldSeparator {
  return (option as SelectFieldSeparator).type === 'separator';
}
function isGroup(option: SelectFieldOption): option is SelectFieldGroup {
  return (option as SelectFieldGroup).type === 'group';
}
function renderItems(items: ReadonlyArray<SelectFieldOption>): ReactNode {
  return items.map((option): ReactNode => {
    if (isSeparator(option)) {
      return <SelectSeparator key={`separator-${option.key}`} />;
    }
    if (isGroup(option)) {
      return <SelectGroup key={`group-${option.label}`}>
          {/* Morfi typography (replaces stock's text-xs) */}
          <SelectLabel className="text-caption">{option.label}</SelectLabel>
          {renderItems(option.items)}
        </SelectGroup>;
    }
    if (isItem(option)) {
      return <SelectItem key={option.value} value={option.value} {...option.disabled !== undefined ? {
        disabled: option.disabled
      } : {}}
      // Morfi semantic tokens (replaces stock's text-sm + rounded-sm)
      className="rounded-overlay-item text-body">
          {option.label}
        </SelectItem>;
    }
    return null;
  });
}
export function SelectField(t0) {
  const $ = _c(26);
  const {
    label,
    description,
    error,
    size,
    fullWidth,
    required,
    disabled,
    id,
    name,
    value,
    valueLabel,
    onValueChange,
    placeholder,
    items,
    containerClassName,
    labelClassName,
    descriptionClassName,
    errorClassName,
    triggerClassName,
    autoComplete,
    "aria-label": ariaLabel,
    "aria-required": ariaRequired
  } = t0;
  let t1;
  if ($[0] !== ariaLabel || $[1] !== ariaRequired || $[2] !== autoComplete || $[3] !== fullWidth || $[4] !== items || $[5] !== name || $[6] !== onValueChange || $[7] !== placeholder || $[8] !== triggerClassName || $[9] !== value || $[10] !== valueLabel) {
    t1 = t2 => {
      const {
        controlProps
      } = t2;
      return <Select {...value !== undefined ? {
        value
      } : {}} onValueChange={onValueChange} {...controlProps.disabled !== undefined ? {
        disabled: controlProps.disabled
      } : {}} {...controlProps.required !== undefined ? {
        required: controlProps.required
      } : {}} {...name !== undefined ? {
        name
      } : {}}><SelectTrigger id={controlProps.id} {...controlProps["aria-describedby"] !== undefined ? {
          "aria-describedby": controlProps["aria-describedby"]
        } : {}} {...controlProps["aria-invalid"] !== undefined ? {
          "aria-invalid": controlProps["aria-invalid"]
        } : {}} {...autoComplete !== undefined ? {
          autoComplete
        } : {}} {...ariaLabel !== undefined ? {
          "aria-label": ariaLabel
        } : {}} {...ariaRequired !== undefined ? {
          "aria-required": ariaRequired
        } : {}} className={cn("rounded-control text-body-lg", "h-10 border-solid bg-background-primary dark:bg-background-primary shadow-none", "transition-[color,background-color,border-color,box-shadow]", "hover:border-border-brand active:border-border-brand", "disabled:bg-background-secondary dark:disabled:bg-background-secondary", "aria-invalid:hover:border-feedback-error-border aria-invalid:active:border-feedback-error-border", fullWidth !== false && "w-full", triggerClassName)}><SelectValue {...placeholder !== undefined ? {
            placeholder
          } : {}}>{valueLabel}</SelectValue></SelectTrigger><SelectContent className="z-popover shadow-elevation-popover">{renderItems(items)}</SelectContent></Select>;
    };
    $[0] = ariaLabel;
    $[1] = ariaRequired;
    $[2] = autoComplete;
    $[3] = fullWidth;
    $[4] = items;
    $[5] = name;
    $[6] = onValueChange;
    $[7] = placeholder;
    $[8] = triggerClassName;
    $[9] = value;
    $[10] = valueLabel;
    $[11] = t1;
  } else {
    t1 = $[11];
  }
  let t2;
  if ($[12] !== containerClassName || $[13] !== description || $[14] !== descriptionClassName || $[15] !== disabled || $[16] !== error || $[17] !== errorClassName || $[18] !== fullWidth || $[19] !== id || $[20] !== label || $[21] !== labelClassName || $[22] !== required || $[23] !== size || $[24] !== t1) {
    t2 = <FieldShell label={label} description={description} error={error} size={size} fullWidth={fullWidth} required={required} disabled={disabled} id={id} containerClassName={containerClassName} labelClassName={labelClassName} descriptionClassName={descriptionClassName} errorClassName={errorClassName}>{t1}</FieldShell>;
    $[12] = containerClassName;
    $[13] = description;
    $[14] = descriptionClassName;
    $[15] = disabled;
    $[16] = error;
    $[17] = errorClassName;
    $[18] = fullWidth;
    $[19] = id;
    $[20] = label;
    $[21] = labelClassName;
    $[22] = required;
    $[23] = size;
    $[24] = t1;
    $[25] = t2;
  } else {
    t2 = $[25];
  }
  return t2;
}